/**
 * Kindra's icon geometry (ADR-W01: icons come from the design, not an icon
 * library). Generated from the Android icon set — mobile/src/components/icons/
 * Icon.tsx and NavIcons.tsx — whose paths are copied verbatim from
 * DesignReference/Main.dc.html (see docs/icon-map.md for the design lines).
 * This file is data only; web/src/ui/kit/Icon.tsx renders it as SVG.
 */
export type Paint = 'color' | 'none' | (string & {});

export interface ShapeStyle {
  /** 'color' = the icon colour (currentColor); a literal is used as-is. Default: none (or the colour when filled). */
  fill?: Paint;
  /** 'color' (default) = the icon colour; a literal or 'none' overrides it. */
  stroke?: Paint;
  /** Overrides the icon stroke width for this shape (source-specific widths). */
  sw?: number;
}

export type Shape = ShapeStyle &
  (
    | { k: 'path'; d: string }
    | { k: 'circle'; cx: number; cy: number; r: number }
    | { k: 'rect'; x: number; y: number; w: number; h: number; rx?: number }
    | { k: 'polyline'; points: string }
  );

export interface IconDef {
  /** Source viewBox when it is not the default `0 0 24 24`. */
  viewBox?: string;
  /** Rotation in degrees about the viewBox centre (e.g. the hang-up handset). */
  rotate?: number;
  shapes: Shape[];
}

export const ICONS = {
  close: {
    shapes: [{ k: 'path', d: 'M18 6L6 18M6 6l12 12' }],
  },
  // Web only: the "more actions" menu trigger (Android uses swipe and long-press instead).
  more: {
    shapes: [
      { k: 'circle', cx: 12, cy: 5, r: 1.5, fill: 'color' },
      { k: 'circle', cx: 12, cy: 12, r: 1.5, fill: 'color' },
      { k: 'circle', cx: 12, cy: 19, r: 1.5, fill: 'color' },
    ],
  },
  bell: {
    shapes: [
      { k: 'path', d: 'M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9' },
      { k: 'path', d: 'M13.7 21a2 2 0 0 1-3.4 0' },
    ],
  },
  heart: {
    shapes: [
      {
        k: 'path',
        d: 'M20.8 4.6a4.8 4.8 0 0 0-6.8 0L12 6.6l-2-2a4.8 4.8 0 1 0-6.8 6.8l8.8 8.8 8.8-8.8a4.8 4.8 0 0 0 0-6.8z',
      },
    ],
  },
  'heart-filled': {
    shapes: [
      {
        k: 'path',
        d: 'M12 21s-7.2-4.7-9.7-9C.6 8.7 2.1 5.2 5.5 4.5c2.1-.4 4.1.5 5.2 2.2C11.8 5 13.8 4.1 15.9 4.5c3.4.7 4.9 4.2 3.2 7.5C16.6 16.3 12 21 12 21z',
        fill: 'color',
        stroke: 'none',
      },
    ],
  },
  'shield-check': {
    shapes: [
      { k: 'path', d: 'M12 3l7 3v5c0 4.6-3 8.4-7 9.6-4-1.2-7-5-7-9.6V6l7-3z' },
      { k: 'path', d: 'M9 12l2 2 4-4.2' },
    ],
  },
  'circle-check': {
    shapes: [
      { k: 'circle', cx: 12, cy: 12, r: 9 },
      { k: 'path', d: 'M8 12.5l2.6 2.6L16.2 9' },
    ],
  },
  check: {
    shapes: [{ k: 'polyline', points: '20 6 9 17 4 12' }],
  },
  'check-sent': {
    viewBox: '0 0 16 11',
    shapes: [{ k: 'path', d: 'M1 5.5L5.5 10 15 1' }],
  },
  'check-read': {
    viewBox: '0 0 20 11',
    shapes: [
      { k: 'path', d: 'M1 5.5L5.5 10 13 2.5' },
      { k: 'path', d: 'M6 5.5L10.5 10 19 1' },
    ],
  },
  eye: {
    shapes: [
      { k: 'path', d: 'M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z' },
      { k: 'circle', cx: 12, cy: 12, r: 3 },
    ],
  },
  'eye-off': {
    shapes: [
      { k: 'path', d: 'M17.9 17.9A10.9 10.9 0 0 1 12 19C5 19 1 12 1 12a18.5 18.5 0 0 1 5.1-5.9M9.9 4.2A9.1 9.1 0 0 1 12 5c7 0 11 7 11 7a18.5 18.5 0 0 1-2.2 3.2M14.1 14.1a3 3 0 1 1-4.2-4.2' },
      { k: 'path', d: 'M1 1l22 22' },
    ],
  },
  search: {
    shapes: [
      { k: 'circle', cx: 11, cy: 11, r: 7 },
      { k: 'path', d: 'M21 21l-4.3-4.3' },
    ],
  },
  phone: {
    shapes: [
      {
        k: 'path',
        d: 'M4.3 4.6c-.5 3.7 1 8.3 4 11.3s7.6 4.5 11.3 4c.5 0 .9-.4.9-1v-2.7a1 1 0 0 0-.8-1l-3-.6a1 1 0 0 0-1 .3l-1 1.3a13 13 0 0 1-6-6l1.3-1a1 1 0 0 0 .3-1l-.6-3a1 1 0 0 0-1-.8H5.3c-.6 0-1 .4-1 1z',
      },
    ],
  },
  'hang-up': {
    rotate: 135,
    shapes: [
      {
        k: 'path',
        d: 'M4.3 4.6c-.5 3.7 1 8.3 4 11.3s7.6 4.5 11.3 4c.5 0 .9-.4.9-1v-2.7a1 1 0 0 0-.8-1l-3-.6a1 1 0 0 0-1 .3l-1 1.3a13 13 0 0 1-6-6l1.3-1a1 1 0 0 0 .3-1l-.6-3a1 1 0 0 0-1-.8H5.3c-.6 0-1 .4-1 1z',
      },
    ],
  },
  'phone-alt': {
    shapes: [
      {
        k: 'path',
        d: 'M6.6 10.8a15.4 15.4 0 0 0 6.6 6.6l2.2-2.2a1 1 0 0 1 1-.25 8.6 8.6 0 0 0 2.7.43 1 1 0 0 1 1 1v2.66a1 1 0 0 1-1 1A17.6 17.6 0 0 1 3.4 3.5a1 1 0 0 1 1-1H7a1 1 0 0 1 1 1 8.6 8.6 0 0 0 .43 2.7 1 1 0 0 1-.25 1z',
      },
    ],
  },
  'phone-outline': {
    shapes: [
      {
        k: 'path',
        d: 'M4 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L14 13l5 2v4a2 2 0 0 1-2 2A15 15 0 0 1 4 6a2 2 0 0 1 2-2z',
      },
    ],
  },
  plus: {
    shapes: [{ k: 'path', d: 'M12 5v14M5 12h14' }],
  },
  'chevron-left': {
    shapes: [{ k: 'path', d: 'M15 18l-6-6 6-6' }],
  },
  'chevron-right': {
    shapes: [{ k: 'path', d: 'M9 18l6-6-6-6' }],
  },
  'video-alt': {
    shapes: [
      { k: 'rect', x: 2.5, y: 6.5, w: 13, h: 11, rx: 2.2 },
      { k: 'path', d: 'M15.5 10.2l6-3.4v10.4l-6-3.4z' },
    ],
  },
  'video-call': {
    shapes: [
      { k: 'rect', x: 3.5, y: 5.5, w: 13, h: 11, rx: 2 },
      { k: 'path', d: 'M16.5 9.5l4-2.5v8l-4-2.5' },
    ],
  },
  video: {
    shapes: [
      { k: 'rect', x: 3, y: 5, w: 15, h: 14, rx: 2 },
      { k: 'path', d: 'M18 10l4-2.5v9L18 14' },
    ],
  },
  'user-plus': {
    shapes: [
      { k: 'circle', cx: 12, cy: 8, r: 4 },
      { k: 'path', d: 'M4 21c0-4 3.6-6.5 8-6.5' },
      { k: 'path', d: 'M17 14v6M14 17h6' },
    ],
  },
  'user-add': {
    shapes: [
      { k: 'circle', cx: 9, cy: 8, r: 3.2 },
      { k: 'path', d: 'M3 20c0-3.5 2.7-6 6-6s6 2.5 6 6' },
      { k: 'path', d: 'M17 8h4M19 6v4' },
    ],
  },
  tag: {
    shapes: [
      {
        k: 'path',
        d: 'M20.6 12.6L12.6 20.6a2 2 0 0 1-2.8 0L3.4 14.2a2 2 0 0 1 0-2.8L11.4 3.4a2 2 0 0 1 1.4-.6H19a2 2 0 0 1 2 2v6.6a2 2 0 0 1-.4 1.6z',
      },
      { k: 'circle', cx: 16, cy: 8, r: 1.5 },
    ],
  },
  calendar: {
    shapes: [
      { k: 'rect', x: 3, y: 5, w: 18, h: 16, rx: 2 },
      { k: 'path', d: 'M3 10h18M8 3v4M16 3v4' },
    ],
  },
  'calendar-small': {
    shapes: [
      { k: 'rect', x: 3, y: 4.5, w: 18, h: 16, rx: 2 },
      { k: 'path', d: 'M3 9.5h18M8 3v3M16 3v3' },
    ],
  },
  'qr-code': {
    shapes: [
      { k: 'rect', x: 3, y: 3, w: 7, h: 7 },
      { k: 'rect', x: 14, y: 3, w: 7, h: 7 },
      { k: 'rect', x: 3, y: 14, w: 7, h: 7 },
      { k: 'path', d: 'M14 14h3v3h-3zM19 14h2v2h-2zM14 19h2v2h-2zM19 19h2v2h-2z' },
    ],
  },
  'qr-scan-frame': {
    shapes: [
      { k: 'rect', x: 3, y: 3, w: 18, h: 18, rx: 2 },
      { k: 'rect', x: 7, y: 7, w: 4, h: 4 },
      { k: 'rect', x: 13, y: 7, w: 4, h: 4 },
      { k: 'rect', x: 7, y: 13, w: 4, h: 4 },
    ],
  },
  'map-pin-filled': {
    shapes: [
      {
        k: 'path',
        d: 'M12 2C7.6 2 4 5.6 4 10c0 6 8 12 8 12s8-6 8-12c0-4.4-3.6-8-8-8z',
        fill: 'color',
        stroke: '#FFF9F4',
        sw: 1,
      },
      { k: 'circle', cx: 12, cy: 10, r: 3, fill: '#FFF9F4', stroke: '#FFF9F4', sw: 1 },
    ],
  },
  laugh: {
    shapes: [
      { k: 'circle', cx: 12, cy: 12, r: 9.5, fill: '#E8B23C', stroke: '#8A6A1E', sw: 1 },
      { k: 'path', d: 'M8 10c.5-1 1.5-1 2 0M14 10c.5-1 1.5-1 2 0', stroke: '#5C4416', sw: 1.4 },
      { k: 'path', d: 'M7.5 13.5c1.2 2 3 3 4.5 3s3.3-1 4.5-3', fill: '#5C4416', stroke: 'none' },
    ],
  },
  'thumbs-up': {
    shapes: [
      {
        k: 'path',
        d: 'M7 11v9H4v-9zM7 11l3.5-7a2 2 0 0 1 3.6 1.4L13 9h5.2a2 2 0 0 1 2 2.4l-1.4 6A2 2 0 0 1 16.8 19H7',
      },
    ],
  },
  'camera-badge': {
    shapes: [
      {
        k: 'path',
        d: 'M4 8a2 2 0 0 1 2-2h1.2l1-1.6h7.6l1 1.6H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      },
      { k: 'circle', cx: 12, cy: 13, r: 3.2 },
    ],
  },
  camera: {
    shapes: [
      {
        k: 'path',
        d: 'M4 8a2 2 0 0 1 2-2h1.2l1-1.6h7.6l1 1.6H18a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z',
      },
      { k: 'circle', cx: 12, cy: 13, r: 3.4 },
    ],
  },
  envelope: {
    shapes: [
      { k: 'rect', x: 3, y: 5, w: 18, h: 14, rx: 2 },
      { k: 'path', d: 'M3 6.5l9 6.5 9-6.5' },
    ],
  },
  'map-pin': {
    shapes: [
      { k: 'path', d: 'M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z' },
      { k: 'circle', cx: 12, cy: 10, r: 3 },
    ],
  },
  'id-card': {
    shapes: [
      { k: 'rect', x: 3, y: 5, w: 18, h: 14, rx: 2 },
      { k: 'circle', cx: 8.5, cy: 12, r: 2 },
      { k: 'path', d: 'M13.5 10h5M13.5 14h3.5' },
    ],
  },
  moon: {
    shapes: [{ k: 'path', d: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z' }],
  },
  shield: {
    shapes: [{ k: 'path', d: 'M12 3l7 3.5v5c0 4.6-3 8.4-7 9.5-4-1.1-7-4.9-7-9.5v-5z' }],
  },
  logout: {
    shapes: [
      { k: 'path', d: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4' },
      { k: 'path', d: 'M16 17l5-5-5-5' },
      { k: 'path', d: 'M21 12H9' },
    ],
  },
  archive: {
    shapes: [{ k: 'path', d: 'M4 7h16M6 7v11a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7M3 4h18v3H3z' }],
  },
  lock: {
    shapes: [
      { k: 'rect', x: 4, y: 9, w: 16, h: 11, rx: 2 },
      { k: 'path', d: 'M8 9V6.5a4 4 0 0 1 8 0V9' },
    ],
  },
  people: {
    shapes: [
      { k: 'path', d: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2' },
      { k: 'circle', cx: 10, cy: 7, r: 4 },
      { k: 'path', d: 'M23 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8' },
    ],
  },
  'warning-triangle': {
    shapes: [
      { k: 'path', d: 'M12 9v4M12 17h.01' },
      {
        k: 'path',
        d: 'M10.3 3.9L2.5 17a2 2 0 0 0 1.7 3h15.6a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z',
      },
    ],
  },
  'warning-triangle-rounded': {
    shapes: [
      { k: 'path', d: 'M12 9v4.5M12 17h.01' },
      {
        k: 'path',
        d: 'M10.3 3.9L2.6 17.5a1.6 1.6 0 0 0 1.4 2.4h16a1.6 1.6 0 0 0 1.4-2.4L13.7 3.9a1.6 1.6 0 0 0-2.8 0z',
      },
    ],
  },
  activity: {
    shapes: [{ k: 'path', d: 'M22 12h-4l-3 9L9 3l-3 9H2' }],
  },
  'bar-chart': {
    shapes: [{ k: 'path', d: 'M4 20V10M11 20V4M18 20v-7' }],
  },
  building: {
    shapes: [{ k: 'path', d: 'M3 21h18M4 21V9l8-6 8 6v12M9 21v-6h6v6' }],
  },
  mic: {
    shapes: [
      { k: 'rect', x: 9, y: 2.5, w: 6, h: 11, rx: 3 },
      { k: 'path', d: 'M5.5 11a6.5 6.5 0 0 0 13 0M12 17.5V21' },
    ],
  },
  // Call controls (not in the design): a solid microphone / camera, and the same crossed out for "off".
  'call-mic': {
    shapes: [
      { k: 'rect', x: 8.5, y: 2.5, w: 7, h: 12.5, rx: 3.5, fill: 'color' },
      { k: 'path', d: 'M5.5 11.5a6.5 6.5 0 0 0 13 0' },
      { k: 'path', d: 'M12 18v3M8.5 21h7' },
    ],
  },
  'call-mic-off': {
    shapes: [
      { k: 'rect', x: 8.5, y: 2.5, w: 7, h: 12.5, rx: 3.5, fill: 'color' },
      { k: 'path', d: 'M5.5 11.5a6.5 6.5 0 0 0 13 0' },
      { k: 'path', d: 'M12 18v3M8.5 21h7' },
      { k: 'path', d: 'M3.5 3.5l17 17', sw: 2.4 },
    ],
  },
  'call-camera': {
    shapes: [
      { k: 'rect', x: 2.5, y: 6, w: 13.5, h: 12, rx: 3, fill: 'color' },
      {
        k: 'path',
        d: 'M18.5 10.2l3-1.9a.6.6 0 0 1 .9.5v6.4a.6.6 0 0 1-.9.5l-3-1.9z',
        fill: 'color',
      },
    ],
  },
  'call-camera-off': {
    shapes: [
      { k: 'rect', x: 2.5, y: 6, w: 13.5, h: 12, rx: 3, fill: 'color' },
      {
        k: 'path',
        d: 'M18.5 10.2l3-1.9a.6.6 0 0 1 .9.5v6.4a.6.6 0 0 1-.9.5l-3-1.9z',
        fill: 'color',
      },
      { k: 'path', d: 'M3.5 3.5l17 17', sw: 2.4 },
    ],
  },
  // Two arrows chasing each other: switch between the front and rear camera.
  'call-camera-switch': {
    shapes: [
      { k: 'path', d: 'M4.5 10a7.5 7.5 0 0 1 13.4-3.6M19.5 14a7.5 7.5 0 0 1-13.4 3.6' },
      { k: 'path', d: 'M18.5 2.8v3.9h-3.9M5.5 21.2v-3.9h3.9' },
    ],
  },
  play: {
    shapes: [{ k: 'path', d: 'M8 5v14l11-7z', fill: 'color' }],
  },
  pause: {
    shapes: [
      { k: 'rect', x: 6.5, y: 5, w: 3.8, h: 14, rx: 1, fill: 'color' },
      { k: 'rect', x: 13.7, y: 5, w: 3.8, h: 14, rx: 1, fill: 'color' },
    ],
  },
  fingerprint: {
    shapes: [
      {
        k: 'path',
        d: 'M12 3a7 7 0 0 0-7 7v3c0 3 1.3 5.6 3.5 7.3M12 3a7 7 0 0 1 7 7v3c0 1.5-.3 2.8-.9 4M8.5 21a10 10 0 0 0 1.7-5.7V10a1.8 1.8 0 1 1 3.6 0v6',
      },
      { k: 'path', d: 'M12 10v4.3c0 1-.4 1.9-1 2.7' },
    ],
  },
  image: {
    shapes: [
      { k: 'rect', x: 4, y: 4, w: 16, h: 16, rx: 2.5 },
      { k: 'circle', cx: 9, cy: 10, r: 1.6 },
      { k: 'path', d: 'M20 16l-4.5-4.5a2 2 0 0 0-2.8 0L7 17' },
    ],
  },
  document: {
    shapes: [
      { k: 'path', d: 'M7 3h7l4 4v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z' },
      { k: 'path', d: 'M14 3v4h4' },
    ],
  },
  send: {
    shapes: [
      { k: 'path', d: 'M22 2L11 13' },
      { k: 'path', d: 'M22 2l-7 20-4-9-9-4 20-7z' },
    ],
  },

  // Navigation icons (mobile/src/components/icons/NavIcons.tsx): bottom tabs / side navigation and the back button.
  home: {
    shapes: [
      { k: 'path', d: 'M3 11l9-7 9 7' },
      { k: 'path', d: 'M5 10v10h14V10' },
    ],
  },
  browse: {
    shapes: [
      { k: 'circle', cx: 11, cy: 11, r: 7 },
      { k: 'path', d: 'M21 21l-4.3-4.3' },
    ],
  },
  messages: {
    shapes: [
      {
        k: 'path',
        d: 'M21 11.5a8.4 8.4 0 0 1-8.9 8.4 9 9 0 0 1-3-.5L4 21l1.5-4.5A8.4 8.4 0 1 1 21 11.5z',
      },
    ],
  },
  profile: {
    shapes: [
      { k: 'circle', cx: 12, cy: 8, r: 4 },
      { k: 'path', d: 'M4 21c0-4 3.6-6.5 8-6.5s8 2.5 8 6.5' },
    ],
  },
  back: {
    shapes: [{ k: 'path', d: 'M15 18l-6-6 6-6' }],
  },
} satisfies Record<string, IconDef>;

export type IconName = keyof typeof ICONS;
export const ICON_NAMES = Object.keys(ICONS) as IconName[];
